import React, { useState } from 'react';
import { useField } from 'react-final-form';

import { EmptyIllustration } from '@dashboard/components/empty-states/EmptyIllustration';
import LoadingIndicator from '@dashboard/components/loading/LoadingIndicator';
import ButtonLink from '@dashboard/components/mui-atoms/ButtonLink';
import { useLayoutTemplatesListQuery } from '@dashboard/generated/wp/platform';
import { useTemplatesListHref } from '@dashboard/hooks/route/hrefs';
import { NorthEastOutlined } from '@mui/icons-material';
import { Box, Stack, Typography } from '@mui/material';
import RadioTabsWrapper from '@wp-block-builder/components/inputs/RadioTabsWrapper';

import BasicLayoutPropertiesInput from './BasicLayoutPropertiesInput';
import LayoutSelectorTemplateCard from './LayoutSelectorTemplateCard';

const TAB_OPTIONS = [
  {
    label: 'One-off',
    value: 'NONE',
  },
  {
    label: 'Shared',
    value: 'LAYOUT',
  },
];

export default function LayoutSelectorInput() {
  const { value, onChange } = useField('configuration.layoutId').input;
  const [currentLayoutTab, setCurrentLayoutTab] = useState(!value ? 'NONE' : 'LAYOUT');
  const { data } = useLayoutTemplatesListQuery({
    fetchPolicy: 'network-only',
    nextFetchPolicy: 'cache-first',
  });
  const [lastLayoutId, setLastLayoutId] = useState(value);
  const layoutsHref = useTemplatesListHref({ type: 'LAYOUT' });

  const templates = data?.WaypointTemplates.nodes;

  const renderCurrentTab = () => {
    if (currentLayoutTab === 'NONE') {
      return (
        <Box py={2} px={1}>
          <BasicLayoutPropertiesInput />
        </Box>
      );
    }
    if (templates === undefined) {
      return <LoadingIndicator />;
    }
    if (templates.length === 0) {
      return (
        <EmptyIllustration
          caption={
            <>
              <Typography variant="body2" color="text.secondary">
                No shared layouts available. Layouts allow your team to share blocks and styles across templates.
              </Typography>
              <ButtonLink
                size="small"
                endIcon={<NorthEastOutlined fontSize="small" />}
                href={layoutsHref}
                target="_blank"
              >
                Open layouts
              </ButtonLink>
            </>
          }
        />
      );
    }

    return (
      <Box sx={{ maxHeight: 'calc(100vh - 300px)', overflowY: 'auto', position: 'relative' }}>
        <Stack spacing={2} p={1}>
          {templates.map((k) => (
            <LayoutSelectorTemplateCard
              key={k.id}
              active={value === k.id}
              template={k}
              onClick={() => onChange(k.id)}
            />
          ))}
        </Stack>
      </Box>
    );
  };

  const handleTabChange = (v: unknown) => {
    if (v === 'NONE' || v === null) {
      setCurrentLayoutTab('NONE');
      setLastLayoutId(value);
      onChange(null);
    } else {
      setCurrentLayoutTab('LAYOUT');
      if (lastLayoutId) {
        onChange(lastLayoutId);
        return;
      }
      if (templates === undefined) {
        return;
      }
      if (templates.length === 0) {
        return;
      }
      onChange(templates[0].id);
    }
  };

  return (
    <RadioTabsWrapper
      label="Choose layout / theme"
      value={currentLayoutTab}
      options={TAB_OPTIONS}
      onChange={handleTabChange}
    >
      {renderCurrentTab()}
    </RadioTabsWrapper>
  );
}
